import { useState } from "react";
import { useSelector } from "react-redux";

import { useUpdatePatientProfile } from "@/api2/PatientProfile/mutations/useUpdatePatientProfile";
import { CrisisSupportInfo } from "@/components/Consent/CrisisSupportInfo";
import { PhotoIDUpload } from "@/components/Patient/PhotoID/PhotoIDUpload";
import type { PhotoIDUploadStatus } from "@/components/Patient/PhotoID/types";
import { getPhotoIDFileKey } from "@/core/helpers/photoID";
import { showToast, type ToastParams } from "@/providers/ToastProvider";
import { selectPatientId } from "@/redux/selectors/authSelectors";
import { Button2 } from "@/ui2/atoms/Button";
import { Card2 } from "@/ui2/atoms/Card";
import { Divider2 } from "@/ui2/atoms/Divider";
import { Heading2 } from "@/ui2/atoms/Heading";
import { getAutomationDataAttr } from "@/utils/automationUtils";

type UploadPhotoIDProps = {
  onComplete: () => void;
  onSkip: () => void;
};

export function UploadPhotoID({ onComplete, onSkip }: UploadPhotoIDProps) {
  const patientId = useSelector(selectPatientId);
  const [uploadStatus, setUploadStatus] = useState<PhotoIDUploadStatus>("idle");
  const { isError, mutate: updateProfile, status } = useUpdatePatientProfile();

  const isUploadComplete = uploadStatus === "success";

  const handleConfirm = () => {
    updateProfile(
      {
        personal_id_front: getPhotoIDFileKey({ id: patientId, side: "front" }),
        username: patientId,
      },
      {
        onError: () => {
          showToast(errorToast);
        },
        onSuccess: onComplete,
      },
    );
  };

  return (
    <Card2 className="tw:p-12 tw:flex-col tw:gap-8 tw:lg:max-w-140 tw:mx-auto">
      <Heading2 as="h1" className="tw:text-center" size={4}>
        ID Verification
      </Heading2>

      <p className="body-md">
        We need a photo of your government-issued ID to verify your identity. This is legally
        required for all telehealth sessions. It will be securely stored and only accessible to your
        care team.
      </p>

      <PhotoIDUpload onParentStatusChange={setUploadStatus} patientId={patientId} />

      {isUploadComplete && (
        <Button2
          aria-label="Confirm ID upload and proceed"
          isLoading={status === "loading"}
          onPress={handleConfirm}
          {...getAutomationDataAttr("photo-id-confirm-button")}
        >
          {isError ? "Retry" : "Confirm"}
        </Button2>
      )}

      {(!isUploadComplete || isError) && <SkipUploadSection onSkip={onSkip} />}

      <Divider2 />
      <CrisisSupportInfo />
    </Card2>
  );
}

function SkipUploadSection({ onSkip }: { onSkip: () => void }) {
  return (
    <p className="body-md tw:self-start">
      Don't have your ID handy?
      <br />
      <Button2
        aria-label="Skip photo ID upload"
        className="tw:font-normal"
        onPress={onSkip}
        size="md"
        variant="link"
        {...getAutomationDataAttr("photo-id-skip-button")}
      >
        Skip for now
      </Button2>{" "}
      – you can still join your session and add your ID upon next login.
    </p>
  );
}

const errorToast: ToastParams = [
  {
    heading: "Profile update error",
    text: (
      <>
        We couldn't update your patient profile.
        <br />
        Please try again or contact support if the problem persists.
      </>
    ),
    variant: "error",
  },
];
